import { useContext, useMemo } from 'react';

import { getCanvasColor } from '../../core/components/canvas/utils/canvas-color.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../core/types/color-palette.js';
import { buildCategoricalColorScale } from '../../core/utils/build-categorical-color-scale.js';
import { SankeyColorPaletteContext } from '../context/SankeyColorPalette.context.js';
import { SankeyDataContext } from '../context/SankeyData.context.js';
import type { SankeyGraphNode } from '../types/sankey-internal.js';

function buildPerNodeColorMap(
  nodes: SankeyGraphNode[],
  colorPalette: ColorPalette | CustomColorPalette,
): Map<string, string> {
  const ids = nodes.map((n) => n.id);
  const scale = buildCategoricalColorScale(colorPalette, ids);
  const colorMap = new Map<string, string>();
  for (const node of nodes) {
    colorMap.set(node.id, getCanvasColor(scale(node.id)));
  }
  return colorMap;
}

function buildColumnColorMap(
  nodes: SankeyGraphNode[],
  colorPalette: ColorPalette | CustomColorPalette,
): Map<string, string> {
  const uniqueColumns = [
    ...new Set(
      nodes.map((n) => n.column).filter((c): c is number => c !== undefined),
    ),
  ].sort((a, b) => a - b);
  const columnKeys = uniqueColumns.map(String);
  const scale = buildCategoricalColorScale(colorPalette, columnKeys);

  const colorMap = new Map<string, string>();
  for (const node of nodes) {
    const key =
      node.column !== undefined ? String(node.column) : columnKeys[0] ?? '0';
    colorMap.set(node.id, getCanvasColor(scale(key)));
  }
  return colorMap;
}

/**
 * Returns a map from node id to resolved hex color.
 *
 * When nodes carry an explicit column assignment (from `columnAccessor`), all
 * nodes in the same column receive the same categorical palette color so that
 * columns are visually grouped. Without column assignments each node receives
 * its own palette color in document order.
 */
export function useNodeColors(): Map<string, string> {
  const { nodes } = useContext(SankeyDataContext);
  const colorPalette = useContext(SankeyColorPaletteContext);

  return useMemo(() => {
    const hasColumnAssignment = nodes.some((n) => n.column !== undefined);
    return hasColumnAssignment
      ? buildColumnColorMap(nodes, colorPalette)
      : buildPerNodeColorMap(nodes, colorPalette);
  }, [nodes, colorPalette]);
}
